import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit, TemplateRef, ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { TablelistStoreHandlerService } from '../../services/tablelist-store-handler.service';
import { Subject, Subscription } from 'rxjs';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { SimpleNotesComponent } from '../../../notes/components/simple-notes.component/simple-notes.component';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import moment from 'moment';
import { ITablelistExecute, ResourceTypes } from '../../interfaces';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { DynamicApiService } from '../../../export/services/dynamic-api.service';
import { IValuesetItem } from '../../interfaces/valueset.interface';
import { Message, MessageService } from '@wuitk/angular/components/notification';
import { ValuesetService } from '../../services/valueset.service';
import { without, values, set, get, isEmpty } from 'lodash';
import {PermissionSchemaService} from "../../../../ispm-admin/app/org-admin-settings/permissionschema/services/permissionschemas.service";
import { IPermissionSchema } from '../../../../ispm-admin/app/org-admin-settings/permissionschema/components/interfaces/permissionschema';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { WorklistExecute } from '../../classes/worklist-execute.class';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { map, catchError, take } from 'rxjs/operators';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { HttpInterceptorService } from "@ispm/core/services/http-interceptor.service";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Component({
  selector: 'gen-valueset-editor',
  templateUrl: './valueset-editor.component.html',
  styleUrls: ['./valueset-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValuesetEditorComponent implements OnInit, OnDestroy {
  public readonly popups = {
    notes: {
      name: 'Notes',
      notFound: 'Notes not found'
    },
    description: {
      name: 'Description',
      notFound: 'Description not found'
    },
    error: {
      name: 'Error',
      message: 'A value with Display name '
    }
  };
  public actionItemId: string;
  public addBtnLabel: string;
  public allValues: Array<any> = [];
  public enableWrite: boolean = false;
  public executeActions: ITablelistExecute[] = [];
  public items;
  public lastUpdatedDate: string;
  public loading: boolean = true;
  public newTestConfig: any;
  public permissionSchema: IPermissionSchema = null;
  public selectedActiveItems: IValuesetItem[] = [];
  public selectedInactiveItems: IValuesetItem[] = [];
  public selectedRows: any[];
  public values: IValuesetItem[] = [];
  public valueset = null;
  public valuesetActions = {
    activate: {
      text: 'Activate',
      enabled: true,
      headerTitle: 'ht',
      id: 'activate',
      type: '',
      disable: true,
    },
    deactivate: {
      text: 'Deactivate',
      enabled: true,
      headerTitle: 'ht',
      id: 'deactivate',
      type: '',
      disable: true,
    }
  };
  public valuesetName: string;

  private activeValues = { active: 'Active', inactive: 'Inactive'};
  private caseSensitiveSearch: boolean = false;
  private destroyed$: Subject<any> = new Subject();
  private enableEdit: boolean = false;
  private modalSubscriber$: Subscription;
  private notificationMessage;
  private org_id: string;
  private propertiesToSearch = ['display', 'externalCode'];
  private resource: string = 'valueset';
  private userRole: string;
  private valuesetLocale: string;

  @ViewChild('notification') public notification: TemplateRef<any>;

  constructor(
    public notificationMessageService: NotificationMessageService,
    private _formHandler: FormHandlerService,
    private _changeDetector: ChangeDetectorRef,
    private _ngZone: NgZone,
    private _userActionCreatorService: UserActionCreatorService,
    private dynamicApiService: DynamicApiService,
    private dynamicModalLoader: DynamicModalLoaderService,
    private messageService: MessageService,
    private authService: AuthenticationService,
    private tablelistStoreHandler: TablelistStoreHandlerService,
    private valuesetService: ValuesetService,
    private permissionSchemaService: PermissionSchemaService,
    private _http: HttpInterceptorService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private routerManagerService: RouterManagerService
  ) {
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  defineActions() {
    const editAction = WorklistExecute.builder('editable', 'Edit', this.openEditValuesetForm.bind(this));
    editAction.enableCondition = this.isEditEnabled.bind(this);
    this.executeActions = [editAction];
  }

  isEditEnabled(): boolean {
    return this.enableEdit;
  }

  defineDisabledForEditFields(row_data, isProvider) {
    const disabledFields: string[] = [];

    if (this.valueset.ontology) {
      if (this.userRole !== 'GenomicsAdminGroup') {
        // not system admin
        disabledFields.push(...['display', 'externalCode']);
      }
    } else {
      const data = this.valueset.define ? this.valueset.define.concept : this.valueset.concept;
      const item = data && data.find(itm => itm.code === row_data.code);
      if (item.source === 'Default') {
        if ((this.userRole !== 'GenomicsAdminGroup') && !isProvider) {
          disabledFields.push('display');
        }
      }
    }

    return disabledFields;
  }

  openEditValuesetForm(self) {
    if (self.rowData) {
      if (!self.rowData.externalCode) {
        self.rowData.externalCode = self.rowData.code;
      }

      if (self.rowData.isActive) {
        self.rowData.active = self.rowData.isActive === this.activeValues.active ? 'true' : 'false';
      }
    }

    this.valuesetService.getValuesetProvider(this.org_id)
      .pipe(take(1))
      .subscribe(result => {
        const providers = result.body;
        // Currrently: an organization can only be OR consumer OR provider.
        // So if providers is empty - organization is NOT consumer, but provider.
        // see GEN-91343
        const isProvider: boolean = providers.length === 0;

        const payload = {
          title: self.payload.title || '',
          formId: self.payload.formId,
          approveBtn: self.payload.approveBtn || 'Confirm',
          declineBtn: self.payload.declineBtn || 'Cancel',
          paramsToSet: self.rowData,
          disabledFields: this.defineDisabledForEditFields(self.rowData, isProvider),
          note: self.payload.note,
          editMode: false
        };
        this.openPopupModal(event, payload);
      });
  }

  ngOnInit(): void {
    this.loading = true;
    this.valuesetName = this.routerManagerService.parameters.name;
    this.valuesetLocale = this.activatedRoute.snapshot.queryParams['locale'];
    this.loadConfiguration(this.resource);

    this._userActionCreatorService.getCurrentUser().subscribe(user => {
      this.org_id = user.organization;
      this.userRole = user.role;
      this.valuesetService.getValuesetItems(this.valuesetName, this.valuesetLocale, this.org_id)
        .subscribe(
          res => {
            this.extractValuesetData(res);

            // this.enableEdit = user.permissions && user.permissions.some(itm => ['ADMINVALUESET.EDIT'].includes(itm));
            this.enableEdit = user.permissions && user.permissions.includes('ADMINVALUESET.EDIT') && user.permissions.includes('ADMINVALUESET.WRITE');
            // this.enableWrite = user.permissions && user.permissions.some(itm => ['ADMINVALUESET.WRITE'].includes(itm));
            this.enableWrite = user.permissions && user.permissions.includes('ADMINVALUESET.WRITE');
            this.defineActions();

            this.tablelistStoreHandler.resolveConfigurationTemplate({
              resourceType: ResourceTypes.Generic,
              configTemplateType: 'valueset',
            }).toPromise()
              .then(() => {
                this.loading = false;
                this._changeDetector.detectChanges();
              });
          },
          () => {
            console.log('Failed to get the valueset ${this.valuesetName}');
          }
        );
    });
  }

  private extractValuesetData(response) {
    this.valueset = response.json();
    this.values = this.getValues(this.valueset);
    this.lastUpdatedDate = this.valueset && this.valueset.meta && this.valueset.meta.lastUpdated ?
      moment(this.valueset.meta.lastUpdated).format('DD MMM YYYY') :
      'N/A';
  }

  private getValuesetItems() {
    this.valuesetService.getValuesetItems(this.valuesetName, this.valuesetLocale, this.org_id)
      .subscribe(
        res => {
          this.extractValuesetData(res);

          this.loading = false;
          this._changeDetector.detectChanges();
        },
        () => {
          console.log('Failed to get the valueset ${this.valuesetName}');
        });
  }

  private loadConfiguration(resource: string) {
    this.tablelistStoreHandler.resolveConfigurationTemplate({
      resourceType: ResourceTypes.Generic,
      configTemplateType: resource,
    }).toPromise()
      .then(config => {
        this.newTestConfig = get(config, 'newTestConfig', null);
        if (this.newTestConfig) {
          this.addBtnLabel = this.newTestConfig.buttonLabel;
        }
      });
  }

  private getValues(valueset): IValuesetItem[] {
    let valuePath = this.findValuePath(valueset);
    if (valuePath == null) {
      valuePath = 'define.concept';
    }
    this.allValues = get(valueset, valuePath) || [];
    const ret: IValuesetItem[] = [...this.allValues];
    ret.forEach(value => {
      const inactive = valueset.inactive && valueset.inactive.includes(value.code);
      value.isActive = inactive ? this.activeValues.inactive : this.activeValues.active;
      if (valueset.ontology) {
        value.source = valueset.ontology;
      } else if (value.owner) {
        value.source = (value.owner === this.org_id) ? 'Local' : 'Provider';
      } else {
        value.source = 'Default';
      }
      if (!value.externalCode) {
        value.externalCode = value.code;
      }
    });
    return ret;
  }

  redirectToDashboard() {
    let type = 'admin' + (this.valueset && this.valueset.resourceType || 'ValueSet');
    this.router.navigate(['../../dashboard', type], {relativeTo: this.activatedRoute});
  }

  getValuesetName(): string {
    return this.valueset && this.valueset.name ? this.valueset.name : '';
  }

  disablePopup(param: string): string  {
    let enabledPopup: boolean = true;
    const clsName: string  = 'disabled-popup';

    switch (param) {
      case this.popups.notes.name:
        enabledPopup = this.valueset && this.valueset.note;
        break;

      case this.popups.description.name:
        enabledPopup = this.valueset && this.valueset.description;
        break;
    }

    return enabledPopup ? '' : clsName;
  }

  getTooltip(param: string): string {
    let ttip: string = '';

    switch (param) {
      case this.popups.notes.name:
        ttip = this.valueset && this.valueset.note ? this.popups.notes.name : this.popups.notes.notFound;
        break;

      case this.popups.description.name:
        ttip = this.valueset && this.valueset.description ? this.popups.description.name : this.popups.description.notFound;
        break;
    }
    return ttip;
  }

  openValuesetPopup(param, event) {
    let title: string = '',
      content: string = '';

    switch (param) {
      case this.popups.notes.name:
        title = this.popups.notes.name;
        content = this.valueset.note ? this.valueset.note : this.popups.notes.notFound;
        break;
      case this.popups.description.name:
        title = this.popups.description.name;
        content = this.valueset.description ? this.valueset.description : this.popups.description.notFound;
        break;
    }

    this.openPopup(event, title, content);
  }

  openPopup(event, title: string, content: string) {
    event.stopPropagation();

    if (this.modalSubscriber$) {
      return;
    }

    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent,
      {
        componentToLoad: SimpleNotesComponent,
        classes: 'simple-note-modal-container ',
        title: title,
        payload: {
          notes: content
        },
        // approveBtn: 'Save',
        declineBtn: 'Close'
      }, ModalTypes.CUSTOM)
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe((response: any) => {
          switch (<modalResponse> response.status) {
            case modalResponse.approve:
              break;
            case modalResponse.decline:
            default:
              return false;
          }
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        },
        () => {
        },
        () => {
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        });
  }

  onSearchChange(searchStr: string) {
  }

  onSearchClear(searchStr: string) {
    this.onSearchExecute(searchStr);
  }

  onSearchExecute(searchStr: string) {
    if (!!searchStr) {
      let search = !this.caseSensitiveSearch ? searchStr.toLowerCase() : searchStr;
      this.values = this.getValues(this.valueset).filter(value => this.matches(value, search));
    } else {
      this.values = this.getValues(this.valueset);
    }
  }

  addNewItem(event) {
    this.openPopupModal(event, { ...this.newTestConfig.payload, ...{ editMode: true}});
  }

  popupDynamicFormModal(title: string, formId: string, formData: any, approveBtn?: string, declineBtn?: string,
                        channelName?: string, classes?: string, disabledFields?: Array<string>, note?: string,
                        editMode: boolean = false) {
    return this._formHandler.popupDynamicFormModal(title, formId, formData, approveBtn, declineBtn,
    { 'classes': classes ? classes + ' ' : ''} ,
    channelName, null, false, disabledFields, note, editMode);
  }

  openPopupModal(event, payload: any) {
    event && event.stopPropagation();

    if (this.modalSubscriber$) {
      return;
    }

    this.modalSubscriber$ = this.popupDynamicFormModal(
      payload.title || '',
      payload.formId,
      payload.paramsToSet, // formData
      payload.approveBtn || 'Confirm',
      payload.declineBtn || 'Cancel',
      payload.channelName,
      'add-new-valueset-value-class create-valueset-modal',
      payload.disabledFields,
      payload.note,
      payload.editMode
    ).filter((response: any) => response.payload)
      .subscribe((response: any) => {
          switch (<modalResponse> response.status) {
            case modalResponse.approve:
              const formData = get(response, 'payload.formData');
              if (formData && (!isEmpty(formData['valueset_value_list']) || !isEmpty(formData['display']))) {
                const itemsArray = formData['valueset_value_list'] || [];
                if (isEmpty(itemsArray)) {
                  itemsArray.push(formData);
                }
                this.createEditValue(itemsArray, event);
              }
              break;
            case modalResponse.decline:
            default:
              return false;
          }
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        },
        () => {
        },
        () => {
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        });
  }

  private findValuePath(vs) {
    const valuePaths = ['define.concept', 'compose.include[0].concept', 'codeSystem.concept', 'concept'];
    for (let i = 0; i < valuePaths.length; i++) {
      const path = valuePaths[i];
      const baseValues = get(vs, path);
      if (baseValues) {
        return path;
      }
    }
    return null;
  }

  private removeElementFromStringArray(arr: [], element: string) {
    arr.forEach((value, index) => {
      if (value === element) { arr.splice(index, 1); }
    });
  }

  private createEditValue(data: any, event) {
      this.valuesetService.getLocalValuesetByName(this.getValuesetName(), this.org_id, this.valuesetLocale)
      .pipe(
        map(res => res.body),
        catchError(() => {
          console.error('Local ValueSet with name: %s, org_id: %s, does not exist', this.getValuesetName(), this.org_id);
          return null;
        })
      ).subscribe(localValueset => {
        const errorContent = [];
        const concept = [];
        const codes = [];
        let valuePath = 'define.concept';
        if (localValueset) {
          valuePath = this.findValuePath(localValueset);
        } else {
          valuePath = this.findValuePath(this.valueset);
        }
        let state = this.valuesetActions.activate.id;
        data.forEach(item => {
          if (!isEmpty(item['display'])) {
            let fieldValuesArray = get(this.valueset, valuePath);
            fieldValuesArray = fieldValuesArray.filter(val => val['code'] !== item['code'] && val['display'] === item['display']);
            if (fieldValuesArray.length > 0) {
              errorContent.push(this.popups.error.message + '"' + item['display'] + '" already exists.');
            } else {
              const valueCode = StringUtils.generateGuid();
              const value = {
                code: item['code'] || valueCode,
                display: item['display'],
                externalCode: item['externalCode'] || item['code'],
                active: item['active'] === 'true'
              };

              if (!value.active) {
                if (localValueset && localValueset.inactive) {
                  localValueset.inactive.push(value.code);
                } else {
                  codes.push(value.code);
                }
                state = this.valuesetActions.deactivate.id;
              } else if (localValueset && localValueset.inactive) {
                this.removeElementFromStringArray(localValueset.inactive, value.code);
              }

              concept.push(value);
              if (localValueset) {
                const baseValues = get(localValueset, valuePath);
                const itemIndex = baseValues.findIndex(itm => itm.code === value.code);
                if (itemIndex > -1) {
                  baseValues[itemIndex] = value;
                } else {
                  baseValues.push(value);
                }
              }
            }
          }
        });
        if (concept.length > 0) {
          if (localValueset) { // update
            if (!localValueset.inactive && codes.length > 0) {
              localValueset['inactive'] = codes;
            }
            this.valuesetService.updateValueSetValue({
              valueset: localValueset,
              owner: localValueset.owner
            }).subscribe(() => {
              this.updateValuesetValuesList(state);
            });
          } else { // create new valueset
            const vsData = {
              name: this.valueset.name,
              owner: this.org_id,
              locale: this.valueset.locale,
              inactive: codes
            };
            set(vsData, valuePath, concept);
            this.valuesetService.createValueSetValues(vsData).subscribe(() => {
              this.getValuesetItems();
            });
          }
        }
        if (errorContent.length > 0) {
          this.notificationMessageService.error(errorContent.join('<br>'));
        }
      });
  }

///////////////////////////////////////////////
  onAction (evt) {
    this.changeValueActiveState(evt);
  }

  private generateActiveDeactiveCodes(selectedItems: IValuesetItem[], active: boolean, baseValues: any[], values: any[], concept: any[], localValueset) {
    const codes: string[] = selectedItems.map(itm => itm.code);
    codes.forEach(code => {
      if (baseValues && baseValues.length > 0) {
        const itemIndex = baseValues.findIndex(itm => itm.code === code);
        if (itemIndex > -1) {
          baseValues[itemIndex].active = active;
        } else {
          this.addValueItemLocalValueSet(code, values, active, concept);
        }
      } else {
        this.addValueItemLocalValueSet(code, values, active, concept);
      }
      if (localValueset && localValueset.inactive) {
        if (active) {
          this.removeElementFromStringArray(localValueset.inactive, code);
        } else if (localValueset.inactive.length >= 0) {
          localValueset.inactive.push(code);
        }
      }
    });
    return codes;
  }

  private addValueItemLocalValueSet(code, values: any[], active: boolean, concept: any[]) {
    const itemIndex = values.findIndex(itm => itm.code === code);
    if (itemIndex > -1) {
      const item = values[itemIndex];
      const value = {
        code: item['code'],
        display: item['display'],
        externalCode: item['externalCode'] || item['code'],
        active: active
      };
      concept.push(value);
    }
  }

  private updateValuesetValuesList(state: string) {
    if (state === this.valuesetActions.activate.id) {
      this.selectedInactiveItems = [];
      this.valuesetActions.activate.disable = true;
      this.selectedRows = this.selectedActiveItems;
    } else {
      this.selectedActiveItems = [];
      this.valuesetActions.deactivate.disable = true;
      this.selectedRows = this.selectedInactiveItems;
    }
    this.dynamicApiService.setWorklistSelectedRows(this.selectedRows);
    this.getValuesetItems();
  }

  changeValueActiveState(state: string) {
    this.valuesetService.getLocalValuesetByName(this.getValuesetName(), this.org_id, this.valuesetLocale)
    .pipe(
        map(res => res.body),
        catchError(() => {
          console.error('Local ValueSet with name: %s, org_id: %s, does not exist', this.getValuesetName(), this.org_id);
          return null;
        })
    ).subscribe(localValueset => {
      const valueset = localValueset || this.valueset;
      const concept: string[] = [];
      let codes: string[] = [];
      let valuePath = this.findValuePath(valueset);
      if (valuePath == null) {
        valuePath = 'define.concept';
      }
      const baseValues = get(localValueset, valuePath);
      const values = get(this.valueset, valuePath);

      if (state === this.valuesetActions.activate.id) {
        const codesToActive = this.generateActiveDeactiveCodes(this.selectedInactiveItems, true, baseValues, values, concept, localValueset);
        codes = valueset.inactive ? without(valueset.inactive, ...codesToActive) : [];
      } else {
        const codesToInactive = this.generateActiveDeactiveCodes(this.selectedActiveItems, false, baseValues, values, concept, localValueset);
        codes = (valueset.inactive || []).concat(codesToInactive);
      }

      if (localValueset) { // update
        if (!localValueset.inactive && codes.length > 0) {
          localValueset['inactive'] = codes;
        }
        this.valuesetService.updateValueSetValue({
          valueset: localValueset,
          owner: localValueset.owner
        }).subscribe(() => {
          this.updateValuesetValuesList(state);
        });
      } else { // create new valueset
        const vsData = {
          name: this.valueset.name,
          owner: this.org_id,
          locale: this.valueset.locale,
          inactive: codes
        };
        set(vsData, valuePath, concept);
        this.valuesetService.createValueSetValues(vsData).subscribe(() => {
          this.updateValuesetValuesList(state);
        });
      }
    });
  }

  getItems(): IValuesetItem[] {
    let items: IValuesetItem[];

    switch (this.actionItemId) {
      case this.valuesetActions.activate.id:
        items = this.selectedInactiveItems;
        break;
      case this.valuesetActions.deactivate.id:
        items = this.selectedActiveItems;
        break;
    }

    return items;
  }

  getItemsQtty(id): string {
    let info: number = 0;

    switch (id) {
      case this.valuesetActions.activate.id:
        info = this.selectedInactiveItems.length;
        break;
      case this.valuesetActions.deactivate.id:
        info = this.selectedActiveItems.length;
        break;
    }

    return info + '';
  }

  onRowClicked(evt) {
    if (evt && evt.data) {
      const row = evt.data;
      const selectedItems: IValuesetItem[] = this.dynamicApiService.getWorklistSelectedRows();
      const selected = selectedItems.filter(itm => itm.display == row.display && itm.code == row.code);
      if (row.isActive.toLocaleLowerCase() === this.activeValues.active.toLocaleLowerCase()) {
        if (selected.length > 0) {
          if (!this.selectedActiveItems.includes(selected[0])) {
            this.selectedActiveItems.push(selected[0]);
          }
        } else {
          this.selectedActiveItems = this.selectedActiveItems.filter(itm => itm.display != row.display && itm.code != row.code);
        }
        this.valuesetActions.deactivate.disable = !(this.selectedActiveItems.length > 0);
      } else {
        if (selected.length > 0) {
          if (!this.selectedInactiveItems.includes(selected[0])) {
            this.selectedInactiveItems.push(selected[0]);
          }
        } else {
          this.selectedInactiveItems = this.selectedInactiveItems.filter(itm => itm.display != row.display && itm.code != row.code);
        }
        this.valuesetActions.activate.disable = !(this.selectedInactiveItems.length > 0);
      }
    }
  }

  onSelectAllRowsClicked(checked) {
    if (typeof checked == 'boolean') {
      const selectedItems: IValuesetItem[] = this.dynamicApiService.getWorklistSelectedRows();
      this.selectedActiveItems = selectedItems.filter(itm => itm.isActive.toLocaleLowerCase() === this.activeValues.active.toLocaleLowerCase());
      this.selectedInactiveItems = selectedItems.filter(itm => itm.isActive.toLocaleLowerCase() === this.activeValues.inactive.toLocaleLowerCase());

      this.valuesetActions.activate.disable = !(this.selectedInactiveItems.length > 0);
      this.valuesetActions.deactivate.disable = !(this.selectedActiveItems.length > 0);
    }
  }

  onPageChanged(page) {
    this.selectedRows = this.selectedActiveItems.concat(this.selectedInactiveItems);
  }

  showNotification(evt, actionItemId) {
    evt.stopPropagation();

    if (this.notificationMessage) {
      this.close(this.notificationMessage);
    }
    this.actionItemId = actionItemId;

    this.notificationMessage = {
      key: 'template',
      template: this.notification,
      sticky: true,
      closable: true,
      classNames: 'notification-template'
    };

    this.messageService.addAll([this.notificationMessage]);

    this.setNotificationPosition();
  }

  private setNotificationPosition() {
    const tmr = setTimeout(() => {
      const menuItemEl = document.getElementsByClassName('valueset-action-label ' + this.actionItemId)[0];
      const menuItemRect = menuItemEl.getBoundingClientRect();
      const ttEl = document.getElementsByClassName('pt-notification-container')[0];
      const ttRect = ttEl.getBoundingClientRect();
      const cssWidth = 316; // same as in the css for .pt-notification-container class

      ttEl['style'].left = menuItemRect.left - cssWidth - 23 + 'px';
      ttEl['style'].top = menuItemRect.top - (ttRect.height - menuItemRect.height) / 5 + 6 + 'px';
      ttEl['style'].width = cssWidth;

      clearTimeout(tmr);
    });
  }

  close(message: Message) {
    const mes =  message ? message : this.notificationMessage;
    this.notificationMessage = null;
    this.messageService.clearMessage(mes);
  }

  onMenuClosed() {
    this.close(null);
  }

  actionsArray(): any[] {
    return values(this.valuesetActions);
  }

////////////////////////////////////////////////////////

  private matches(value, searchStr) {
    for (const property of this.propertiesToSearch) {
      let val = value[property] || '';
      if (!this.caseSensitiveSearch && typeof val === 'string') {
        val = val.toLowerCase();
      }
      if (typeof val === 'string' && val.toLowerCase().includes(searchStr.toLowerCase())) {
        return true;
      }
    }
    return false;
  }
}
